/**
 * The two decisions the onboarding property correction makes on its own.
 *
 * Both lived inside Onboarding.tsx, a 3,200-line component that cannot be
 * rendered in isolation, so neither had a test. They are the parts worth
 * testing: one decides what the owner is TOLD when the correction is
 * refused, the other decides what they SEE when the write succeeded but the
 * read back did not.
 */

/** Why `owner_update_onboarding_property` refused, as far as the page cares. */
export type CorrectionRefusal = "shared" | "signed" | "invite" | "generic";

/**
 * Map the function's RAISEd name to a reason the screen can answer.
 *
 * The refusals are deterministic and final, so "please try again" is wrong
 * for most of them: an owner of a shared building can retry forever and
 * never succeed. Matched rather than parsed -- anything unrecognised stays
 * `generic` rather than being guessed at, because a wrong specific message
 * is worse than a vague true one.
 */
export function correctionRefusal(message: unknown): CorrectionRefusal {
  const m = String(message ?? "");
  if (m.includes("property_has_other_owners")) return "shared";
  if (m.includes("onboarding_already_signed")) return "signed";
  if (
    m.includes("invalid_or_expired_invite") ||
    m.includes("property_not_on_this_invite")
  ) {
    return "invite";
  }
  return "generic";
}

export type CorrectableProperty = {
  title: string;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  street_name: string | null;
  street_number: string | null;
};

export type CorrectionPatch = {
  title: string;
  street_name: string;
  street_number: string;
  zip_code: string;
  city: string;
};

/**
 * What the card should show when the write succeeded but the re-read did not.
 *
 * Reporting failure would be a lie -- the correction IS saved -- and leaving
 * the old values on screen would be a worse one, because the owner would
 * believe it had not taken. So apply what was sent, field by field, with
 * `address` recomposed by the same rule the function uses: street + house
 * number, each falling back to the stored value, and the whole thing falling
 * back to the stored address when neither part is known.
 *
 * Blank means "not sent", never "clear this field" -- the function treats it
 * the same way, so a title-only correction cannot blank an address here
 * either.
 */
export function applyCorrectionLocally(
  current: CorrectableProperty,
  patch: CorrectionPatch
): CorrectableProperty {
  const pick = (sent: string, stored: string | null) =>
    sent.trim() || stored || "";

  const street = pick(patch.street_name, current.street_name);
  const number = pick(patch.street_number, current.street_number);

  return {
    ...current,
    title: patch.title.trim() || current.title,
    street_name: street || null,
    street_number: number || null,
    zip_code: patch.zip_code.trim() || current.zip_code,
    city: patch.city.trim() || current.city,
    address: [street, number].filter(Boolean).join(" ") || current.address,
  };
}
